import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ConsultBand from "../components/ConsultBand";
import Contact from "../components/Contact";
import DynamicIcon from "../lib/dynamicIcon";
import { useContent } from "../context/ContentContext";
import SEO from "../seo/SEO";
import { SITE_URL, getSiteName, composeTitle } from "../seo/routeConfig";
import { slugify } from "../lib/slug";

// Reusable page structure for every treatment area. Content is admin-driven
// via `content.treatment.areas[]`. The route matches on the area's `slug`
// (auto-generated from `name` when the admin hasn't customized it).
const findArea = (content, slug) => {
  const areas = Array.isArray(content?.treatment?.areas) ? content.treatment.areas : [];
  return areas.find((a) => (a.slug || slugify(a.name)) === slug);
};

// Pull a plain-text excerpt from the body HTML for SEO fallback.
const bodyExcerpt = (html, max = 160) => {
  if (!html) return "";
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > max ? text.slice(0, max - 1).trimEnd() + "…" : text;
};

const buildServiceJsonLd = (area, content) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: `${area.name} Counselling`,
  name: `${area.name} Counselling in Delta & Tsawwassen, BC`,
  description: area?.seo?.description || bodyExcerpt(area?.body_html) || `${area.name} counselling with Karen Boley, MACP, MSEd, CCC.`,
  provider: { "@id": `${SITE_URL}#business`, "@type": "MedicalBusiness", name: getSiteName(content) },
  areaServed: [
    { "@type": "City", name: "Delta" },
    { "@type": "City", name: "Tsawwassen" },
    { "@type": "AdministrativeArea", name: "British Columbia" },
  ],
  url: `${SITE_URL}/treatment-areas/${area.slug || slugify(area.name)}`,
});

const buildFaqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).filter((f) => (f?.question || "").trim() && (f?.answer || "").trim()).map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export const TreatmentAreaPage = () => {
  const { slug } = useParams();
  const { content } = useContent();
  const area = findArea(content, slug);

  const faqs = useMemo(
    () => (area?.faqs || []).filter((f) => (f?.question || "").trim() && (f?.answer || "").trim()),
    [area]
  );

  if (!area) {
    return (
      <div className="bg-cream min-h-screen">
        <SEO path={`/treatment-areas/${slug}`} title={composeTitle("Not found", content)} description="This page may have moved." noindex />
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-32 text-center">
          <h1 className="font-serif text-4xl text-ink">We couldn&apos;t find that.</h1>
          <p className="mt-3 text-ink-muted">The treatment area you&apos;re looking for may have been renamed or removed.</p>
          <Link to="/#treatment" className="mt-6 inline-flex items-center gap-2 rounded-full bg-sage text-white px-6 py-3 font-semibold hover:bg-sage-dark transition-colors">
            <ArrowLeft className="h-4 w-4" /> See all treatment areas
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (area.active === false) {
    return <Navigate to="/#treatment" replace />;
  }

  const seoTitle = composeTitle(area?.seo?.title || `${area.name} Counselling in Delta, BC`, content);
  const seoDescription = area?.seo?.description || bodyExcerpt(area?.body_html) || `${area.name} counselling with Karen Boley in Delta & Tsawwassen, BC.`;

  const jsonLd = [buildServiceJsonLd(area, content)];
  if (faqs.length) jsonLd.push(buildFaqJsonLd(faqs));

  return (
    <div className="bg-cream min-h-screen">
      <SEO
        path={`/treatment-areas/${area.slug || slugify(area.name)}`}
        title={seoTitle}
        description={seoDescription}
        jsonLd={jsonLd}
      />
      <Navbar />

      <main data-testid="treatment-area-page">
        <section className="pt-28 sm:pt-36 pb-16 sm:pb-24">
          <div className="mx-auto max-w-6xl px-5 md:px-8">
            <Link to="/#treatment" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-ink-muted hover:text-ink transition-colors">
              <ArrowLeft className="h-3 w-3" /> All treatment areas
            </Link>

            <div className="mt-8 flex items-center gap-5">
              <span className="inline-flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl bg-sage/12 text-sage-dark shrink-0">
                <DynamicIcon name={area.icon} className="h-8 w-8 sm:h-10 sm:w-10" />
              </span>
              <h1 className="font-serif font-light text-ink text-4xl sm:text-5xl lg:text-6xl leading-tight">
                {area.name}
              </h1>
            </div>

            {area.body_html && (
              <div
                data-testid="area-body-content"
                className="ta-body mt-10 sm:mt-14 text-ink-soft"
                dangerouslySetInnerHTML={{ __html: area.body_html }}
              />
            )}
          </div>
        </section>

        {/* FAQs — hidden entirely when empty */}
        {faqs.length > 0 && (
          <section data-testid="area-faqs" className="bg-white">
            <div className="mx-auto max-w-6xl px-5 md:px-8 py-20 sm:py-28">
              <p className="text-xs uppercase tracking-widest text-ink-muted">FAQs</p>
              <h2 className="mt-3 font-serif font-light text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight">Common questions.</h2>

              <div className="mt-12 divide-y divide-cream-2 border-t border-b border-cream-2">
                {faqs.map((f, i) => (
                  <details key={i} data-testid={`faq-${i}`} className="group py-6">
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                      <span className="font-serif text-xl sm:text-2xl text-ink leading-snug">{f.question}</span>
                      <span className="mt-1 shrink-0 text-sage-dark group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                    </summary>
                    <div className="mt-4 text-base sm:text-lg text-ink-soft leading-relaxed whitespace-pre-line">{f.answer}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Shared consult band — same component as the homepage. Any edits in
          admin -> Content -> Free consult band flow through here automatically. */}
      <ConsultBand />

      {/* Shared contact section — matches the homepage bottom. */}
      <Contact />

      <Footer />
    </div>
  );
};

export default TreatmentAreaPage;
